import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { api, formatPrice, logger } from "../lib/api";
import { ProductVisual } from "../components/ProductVisual";
import { ProductCard } from "../components/ProductCard";
import { WishlistButton } from "../components/WishlistButton";
import { ReviewsSection } from "../components/ReviewsSection";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const NotFoundView = () => (
  <div className="pt-44 pb-32 text-center">
    <p className="font-display text-2xl text-capra-ivory mb-4">This piece is no longer available.</p>
    <Link to="/new-collection" className="text-capra-bronze text-sm gold-underline">Explore the collection</Link>
  </div>
);

const LoadingView = () => (
  <div className="pt-32 px-6 lg:px-10 max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12">
    <div className="aspect-[3/4] bg-capra-surface animate-pulse" />
    <div className="space-y-4 pt-8">
      <div className="h-8 w-2/3 bg-capra-surface animate-pulse" />
      <div className="h-5 w-1/4 bg-capra-surface animate-pulse" />
    </div>
  </div>
);

const Breadcrumb = ({ product }) => (
  <nav className="text-[11px] uppercase tracking-[0.2em] text-capra-muted mb-8" data-testid="breadcrumb">
    <Link to="/" className="hover:text-capra-bronze transition-colors">Home</Link>
    <span className="mx-2 text-capra-bronze/50">/</span>
    <Link to={`/${product.category}`} className="hover:text-capra-bronze transition-colors">{product.category.replace("-", " ")}</Link>
    <span className="mx-2 text-capra-bronze/50">/</span>
    <span className="text-capra-ivory/70">{product.name}</span>
  </nav>
);

const OptionGroup = ({ label, options, value, onChange, testId, optionTestId, minWidth = "" }) => (
  <div className="mt-6">
    <p className="text-[10px] uppercase tracking-[0.25em] text-capra-muted mb-3">{label}</p>
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o}
          data-testid={optionTestId(o)}
          onClick={() => onChange(o)}
          className={`${minWidth} px-4 py-2 text-xs border transition-colors ${
            value === o ? "border-capra-bronze text-capra-bronze" : "border-white/15 text-capra-muted hover:border-white/40"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const QuantityPicker = ({ qty, setQty }) => (
  <div className="flex items-center border border-white/15">
    <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 text-capra-muted hover:text-capra-ivory" aria-label="Decrease">
      <Minus size={14} />
    </button>
    <span className="px-3 text-sm text-capra-ivory" data-testid="qty-value">{qty}</span>
    <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(10, q + 1))} className="px-3 text-capra-muted hover:text-capra-ivory" aria-label="Increase">
      <Plus size={14} />
    </button>
  </div>
);

const SPEC_ROWS = (product) => [
  { label: "Material", value: product.materials },
  { label: "Construction", value: product.description },
  { label: "Fit", value: product.fit },
  { label: "Details", value: [product.care, "Complimentary insured shipping worldwide, 2–5 business days. Returns accepted within 14 days in original condition, monogram dust bag included."].filter(Boolean).join(" ") },
];

const ProductAccordions = ({ product }) => {
  const rows = SPEC_ROWS(product).filter((r) => r.value);
  return (
    <div className="mt-14" data-testid="product-specs">
      {rows.map((r) => (
        <div key={r.label} className="py-8 border-t border-white/5" data-testid={`spec-${r.label.toLowerCase()}`}>
          <p className="text-[10px] uppercase tracking-[0.45em] text-capra-bronze">{r.label}</p>
          <p className="mt-4 text-sm text-capra-muted leading-loose max-w-md">{r.value}</p>
        </div>
      ))}
    </div>
  );
};

const ProductGallery = ({ product }) => (
  <div className="group relative aspect-[3/4] lg:aspect-auto lg:h-[88vh] overflow-hidden bg-capra-surface" data-testid="product-gallery">
    <div className="h-full w-full transition-transform duration-[1.6s] ease-out group-hover:scale-[1.08]">
      <ProductVisual product={product} />
    </div>
    {product.is_new && (
      <span className="absolute top-4 left-4 z-10 px-3 py-1.5 bg-capra-night/80 backdrop-blur border border-capra-bronze/40 text-[10px] uppercase tracking-[0.25em] text-capra-bronze">
        New Collection
      </span>
    )}
  </div>
);

const ProductHeader = ({ product }) => (
  <>
    <p className="text-[10px] uppercase tracking-[0.5em] text-capra-bronze mb-4">Limited Release · 2026</p>
    <div className="flex items-start justify-between gap-4">
      <h1 className="font-display text-3xl lg:text-4xl text-capra-ivory leading-tight" data-testid="product-name">{product.name}</h1>
      <WishlistButton product={product} className="mt-1 border border-white/10" />
    </div>
    <p className="mt-4 text-xl text-capra-gold" data-testid="product-price">{formatPrice(product.price)}</p>
    {product.rating_count > 0 && (
      <p className="mt-2 text-xs text-capra-muted">★ {product.rating_avg.toFixed(1)} · {product.rating_count} review{product.rating_count !== 1 ? "s" : ""}</p>
    )}
    <p className="mt-6 text-sm text-capra-muted leading-relaxed">{product.description}</p>
    {product.story && <p className="mt-4 font-display italic text-base text-capra-beige/80">"{product.story}"</p>}
  </>
);

const PurchaseControls = ({ product, qty, setQty, onAdd }) => (
  <>
    <div className="mt-8 flex items-stretch gap-3">
      <QuantityPicker qty={qty} setQty={setQty} />
      <button
        data-testid="add-to-cart-button"
        onClick={onAdd}
        disabled={product.stock <= 0}
        className="flex-1 py-4 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-capra-amber transition-colors disabled:opacity-50"
      >
        {product.stock <= 0 ? "Sold Out" : "Add to Cart"}
      </button>
    </div>
    {product.stock > 0 && product.stock <= 5 && (
      <p className="mt-3 text-xs text-capra-copper" data-testid="low-stock-warning">Only {product.stock} remaining.</p>
    )}
  </>
);

const ProductInfo = ({ product, size, setSize, color, setColor, qty, setQty }) => {
  const { addItem } = useCart();

  const add = () => {
    if (product.sizes?.length > 0 && !size) {
      toast.error("Please select a size.");
      return;
    }
    addItem(product, { size, color, quantity: qty });
    toast.success(`${product.name} added to your cart.`);
  };

  return (
    <div className="lg:py-4">
      <ProductHeader product={product} />

      {product.colors?.length > 0 && (
        <OptionGroup
          label={<>Colour — <span className="text-capra-ivory">{color}</span></>}
          options={product.colors}
          value={color}
          onChange={setColor}
          testId="color-selector"
          optionTestId={(c) => `color-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
        />
      )}

      {product.sizes?.length > 0 && (
        <OptionGroup
          label="Size"
          options={product.sizes}
          value={size}
          onChange={setSize}
          testId="size-selector"
          optionTestId={(s) => `size-option-${s}`}
          minWidth="min-w-[48px]"
        />
      )}

      <PurchaseControls product={product} qty={qty} setQty={setQty} onAdd={add} />

      <ProductAccordions product={product} />
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setQty(1);
    api.get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        setColor(data.colors?.[0] || null);
      })
      .catch(() => setNotFound(true));
    api.get(`/products/${slug}/related`)
      .then(({ data }) => setRelated(data))
      .catch((err) => logger.error("Related products fetch failed", err));
  }, [slug]);

  if (notFound) return <NotFoundView />;
  if (!product) return <LoadingView />;

  return (
    <div className="pt-24 lg:pt-32 pb-24" data-testid={`product-detail-${product.slug}`}>
      <div className="px-6 lg:px-10 max-w-[1400px] mx-auto">
        <Breadcrumb product={product} />

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <ProductGallery product={product} />
          <ProductInfo
            product={product}
            size={size}
            setSize={setSize}
            color={color}
            setColor={setColor}
            qty={qty}
            setQty={setQty}
          />
        </div>

        <ReviewsSection slug={product.slug} ratingAvg={product.rating_avg} ratingCount={product.rating_count} />

        {related.length > 0 && (
          <section className="mt-24" data-testid="related-products-section">
            <h2 className="font-display text-2xl text-capra-ivory mb-10">You May Also Consider</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
